import { ApolloProvider } from '@apollo/client';
import { NextUIProvider } from '@nextui-org/react';
import TawkMessengerReact from '@tawk.to/tawk-messenger-react';
import { AnimatePresence } from 'framer-motion';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { I18nextProvider } from 'react-i18next';
import lazyWithPreload from 'react-lazy-with-preload';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { Logo, LogoContainer, Root } from './App.styles';
import AuthenticatedRoute from './components/routing/AuthenticatedRoute';
import UnauthenticatedRoute from './components/routing/UnauthenticatedRoute';
import { appraisalPath, homePath } from './constants';
import { useAuthContext } from './contexts/AuthContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { SessionProvider } from './contexts/SessionContext';
import { useThemeColorContext } from './contexts/ThemeColorContext';
import { ToasterProvider } from './contexts/ToasterContext';
import { ValuationSummaryProvider } from './contexts/ValuationSummaryContext';
import { VDPProvider } from './contexts/VDPContext';
import { AppContext } from './hooks/AppContext';
import i18n from './locale';
import { GlobalStyles } from './theme/GlobalStyles';
import { darkTheme, getNextUITheme, lightTheme } from './theme/Themes';
import { configureConsumerAmplify, configureDealershipAmplify } from './utils/Amplify';
import { configureFirebase } from './utils/Firebase';
import { configureNewRelic } from './utils/NewRelic';
import CustomToaster from './views/toasts';

configureFirebase();
const IS_CONSUMER_USER = window.location.pathname.includes(appraisalPath);
if (IS_CONSUMER_USER) {
  configureConsumerAmplify();
} else {
  configureDealershipAmplify();
}
export const newRelic = configureNewRelic();

const LoginModule = lazyWithPreload(() => import('./views/auth/LoginPage'));
const SignupModule = lazyWithPreload(() => import('./views/auth/SignupPage'));
const VerifyModule = lazyWithPreload(() => import('./views/auth/VerifyPage'));
const RecoveryMailModule = lazyWithPreload(() => import('./views/auth/RecoveryMailPage'));
const ResetPasswordModule = lazyWithPreload(() => import('./views/auth/ResetPasswordPage'));
const AppContainerModule = lazy(() => import('./views/AppContainer'));
const VehicleCreationProcessModule = lazy(() => import('./views/VehicleCreationProcess'));
const ConsumerVCPModule = lazy(() => import('./views/valuationSummary/ConsumerVCP'));
const ValuationSummaryModule = lazyWithPreload(() => import('./views/valuationSummary'));
const ValuationThankYouModule = lazyWithPreload(() => import('./views/valuationSummary/thankYouPage'));

function App(): any {
  const [themeName, setThemeName] = useState<'light' | 'dark'>('light');
  const [user, setUser] = useState<any | undefined>(undefined);
  const { themeColor } = useThemeColorContext();

  const tawkMessengerRef = useRef(null);

  const theme = themeName === 'dark' ? darkTheme : themeColor ? themeColor : lightTheme;

  const setTawkMessengerVisible = () => {
    tawkMessengerRef.current?.showWidget();
    tawkMessengerRef.current?.maximize();
  };

  const onLoadTawkMessenger = () => {
    if (tawkMessengerRef.current?.onBeforeLoaded()) {
      tawkMessengerRef.current?.hideWidget();
    }
  };

  const onTawkChatHidden = () => {
    tawkMessengerRef.current?.hideWidget();
  };

  const showError = (error: string) => {
    toast.error(error, { duration: Infinity });
  };

  const showAlert = (alert: string) => {
    toast(alert, { duration: Infinity });
  };

  const showSuccess = (message: string) => {
    toast.success(message, { duration: Infinity });
  };

  const { loading, isAuthenticated, client } = useAuthContext();

  const preloadUnauthModules = () => {
    void LoginModule.preload();
    void SignupModule.preload();
    void VerifyModule.preload();
    void RecoveryMailModule.preload();
    void ResetPasswordModule.preload();
    void ValuationSummaryModule.preload();
  };

  useEffect(() => {
    if (loading) {
      // Trigger preloadUnauthModules during the initial rendering of the App component
      preloadUnauthModules();
    }
  }, [loading]);

  const Main = (): any => {
    return isAuthenticated ? <Navigate replace to={homePath} /> : <Navigate replace to="/login" />;
  };

  const AnimatedRoutes = () => {
    const location = useLocation();
    const contextValues = {
      isAuthenticated,
      showError,
      showAlert,
      showSuccess,
      user,
      setUser,
      themeName,
      setThemeName,
      setTawkMessengerVisible,
    };

    return (
      <AppContext.Provider value={contextValues}>
        <NavigationProvider>
          <VDPProvider>
            <OrganizationProvider>
              <AnimatePresence initial={false} exitBeforeEnter={true}>
                {IS_CONSUMER_USER ? (
                  <ValuationSummaryProvider>
                    <Routes location={location}>
                      <Route path={appraisalPath} element={<ConsumerVCPModule />} />
                      <Route path={`${appraisalPath}/thank-you`} element={<ValuationThankYouModule />} />
                    </Routes>
                  </ValuationSummaryProvider>
                ) : (
                  <Routes location={location}>
                    <Route path="/" element={<Main />} />
                    <Route element={<UnauthenticatedRoute />}>
                      <Route path="/login" element={<LoginModule />} />
                      <Route path="/signup" element={<SignupModule />} />
                      <Route path="/verify" element={<VerifyModule />} />
                      <Route path="/recovery-mail" element={<RecoveryMailModule />} />
                      <Route path="/reset-password" element={<ResetPasswordModule />} />
                    </Route>
                    <Route element={<AuthenticatedRoute />}>
                      <Route path="/app/*" element={<AppContainerModule />} />
                      <Route path="/vcp" element={<VehicleCreationProcessModule />} />
                    </Route>
                  </Routes>
                )}
              </AnimatePresence>
            </OrganizationProvider>
          </VDPProvider>
        </NavigationProvider>
      </AppContext.Provider>
    );
  };

  const animation = (
    <LogoContainer>
      <Logo />
    </LogoContainer>
  );

  return (
    client && (
      <I18nextProvider i18n={i18n}>
        <ApolloProvider client={client}>
          <ThemeProvider theme={theme}>
            <NextUIProvider theme={getNextUITheme(theme)}>
              <>
                <GlobalStyles theme={theme} />
                {loading ? (
                  animation
                ) : (
                  <SessionProvider>
                    <Router>
                      <Suspense fallback={animation}>
                        <Root>
                          <ToasterProvider>
                            <AnimatedRoutes />
                            <CustomToaster />
                          </ToasterProvider>
                          {process.env.REACT_APP_TAWK_MESSENGER_ENABLED === 'true' && (
                            <TawkMessengerReact
                              propertyId={process.env.REACT_APP_TAWK_MESSENGER_PROPERTY_ID}
                              widgetId={process.env.REACT_APP_TAWK_MESSENGER_WIDGET_ID}
                              ref={tawkMessengerRef}
                              onLoad={onLoadTawkMessenger}
                              onChatMinimized={onTawkChatHidden}
                            />
                          )}
                        </Root>
                      </Suspense>
                    </Router>
                  </SessionProvider>
                )}
              </>
            </NextUIProvider>
          </ThemeProvider>
        </ApolloProvider>
      </I18nextProvider>
    )
  );
}

export default App;
